/**
 * ProjectEditor - Monaco-based JSON editor for Taurus projects
 *
 * Features:
 * - Full Monaco editor with JSON schema validation
 * - Autocomplete for project fields
 * - Live preview of steps in sidebar
 * - Real-time validation feedback
 */

import { useState, useRef, useCallback, useEffect, memo } from 'react';
import Editor, { loader } from '@monaco-editor/react';
import {
  ArrowLeft,
  Save,
  Play,
  Trash2,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  RotateCcw,
  FileJson,
  Loader2,
  ChevronDown,
  ChevronRight,
  BookOpen
} from 'lucide-react';
import {
  PROJECT_JSON_SCHEMA,
  EMPTY_PROJECT_TEMPLATE,
  formatProjectJson,
  validateProjectJson,
  extractStepSummary
} from '../config/projectSchema';

// Configure Monaco JSON defaults with our schema (only once)
let monacoConfigured = false;

const configureMonaco = (monaco) => {
  if (monacoConfigured) return;

  monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
    validate: true,
    schemas: [
      {
        uri: 'http://verbagpt.com/project-schema.json',
        fileMatch: ['*'],
        schema: PROJECT_JSON_SCHEMA
      }
    ],
    enableSchemaRequest: false,
    allowComments: false
  });

  monacoConfigured = true;
};

// Initialize Monaco loader
loader.init().then(configureMonaco);

// Quick Reference item component
const RefItem = ({ name, type, desc, required }) => (
  <div className="py-1">
    <div className="flex items-center gap-1.5">
      <code className="text-blue-600 dark:text-blue-400 font-mono">{name}</code>
      {required && <span className="text-red-500 text-[10px]">*</span>}
      <span className="text-gray-400 dark:text-gray-500 text-[10px]">{type}</span>
    </div>
    <p className="text-gray-500 dark:text-gray-400 text-[10px] leading-tight">{desc}</p>
  </div>
);

const ProjectEditor = memo(function ProjectEditor({
  project,
  onBack,
  onSave,
  onRun,
  onDelete,
  isDarkMode = true
}) {
  // Editor state
  const editorRef = useRef(null);
  const validationTimerRef = useRef(null);
  const [editorContent, setEditorContent] = useState('');

  // Metadata state
  const [projectName, setProjectName] = useState('');
  const [projectTags, setProjectTags] = useState('');

  // Validation state
  const [validation, setValidation] = useState({ valid: false, errors: [], parsed: null, stepCount: 0 });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // UI state
  const [showPreview, setShowPreview] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showQuickRef, setShowQuickRef] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message: string }

  // Initialize editor content from project
  useEffect(() => {
    const json = project?.json || EMPTY_PROJECT_TEMPLATE;
    const content = formatProjectJson(json);
    setEditorContent(content);
    setProjectName(project?.name || json.project_name || 'New Project');
    setProjectTags(project?.tags?.join(', ') || '');
    setHasUnsavedChanges(project?.isNew || project?.isImported || false);

    // Validate initial content
    const result = validateProjectJson(content);
    setValidation(result);
  }, [project]);

  // Cleanup validation timer on unmount
  useEffect(() => {
    return () => {
      if (validationTimerRef.current) {
        clearTimeout(validationTimerRef.current);
      }
    };
  }, []);

  // Handle editor mount
  const handleEditorMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    configureMonaco(monaco);

    // Format document on mount
    setTimeout(() => {
      editor.getAction('editor.action.formatDocument')?.run();
    }, 100);

    // Focus editor
    editor.focus();
  }, []);

  // Handle editor content change (debounced validation)
  const handleEditorChange = useCallback((value) => {
    setEditorContent(value || '');
    setHasUnsavedChanges(true);

    // Debounce validation to reduce lag while typing/scrolling
    if (validationTimerRef.current) {
      clearTimeout(validationTimerRef.current);
    }
    validationTimerRef.current = setTimeout(() => {
      const result = validateProjectJson(value || '');
      setValidation(result);

      // Update project name from JSON if valid
      if (result.valid && result.parsed?.project_name) {
        setProjectName(result.parsed.project_name);
      }
    }, 300); // 300ms debounce
  }, []);

  // Format document
  const handleFormat = useCallback(() => {
    editorRef.current?.getAction('editor.action.formatDocument')?.run();
  }, []);

  // Reset to original
  const handleReset = useCallback(() => {
    if (!project) return;
    const json = project.json || EMPTY_PROJECT_TEMPLATE;
    const content = formatProjectJson(json);
    setEditorContent(content);
    setValidation(validateProjectJson(content));
    setHasUnsavedChanges(false);
    // Update Monaco editor
    if (editorRef.current) {
      editorRef.current.setValue(content);
      setTimeout(() => {
        editorRef.current.getAction('editor.action.formatDocument')?.run();
      }, 50);
    }
  }, [project]);

  // Save project
  const handleSave = useCallback(async () => {
    if (!validation.valid) return;

    setSaving(true);
    try {
      await onSave({
        id: project?.id || null,
        name: projectName,
        tags: projectTags.split(',').map(t => t.trim()).filter(Boolean),
        json: validation.parsed,
        updatedAt: new Date().toISOString()
      });
      setHasUnsavedChanges(false);
      setToast({ type: 'success', message: project?.id && !project?.isNew ? 'Project updated' : 'Project created' });
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      console.error('Failed to save project:', err);
      setToast({ type: 'error', message: 'Failed to save project' });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setSaving(false);
    }
  }, [validation, project, projectName, projectTags, onSave]);

  // Run project
  const handleRun = useCallback(() => {
    if (!validation.valid) return;
    onRun({
      id: project?.id,
      name: projectName,
      json: validation.parsed
    });
  }, [validation, project, projectName, onRun]);

  // Extract steps for preview
  const steps = validation.parsed ? extractStepSummary(validation.parsed) : [];

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors"
            title="Back to projects"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <FileJson className="h-5 w-5 text-blue-500" />
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              {project?.isNew ? 'New Project' : 'Edit Project'}
            </h1>
          </div>
          {hasUnsavedChanges && (
            <span className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded">
              Unsaved
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleFormat}
            className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Format JSON"
          >
            Format
          </button>
          {project && !project.isNew && (
            <button
              onClick={handleReset}
              className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors"
              title="Reset changes"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!validation.valid || saving}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white text-sm rounded-lg transition-colors"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save
          </button>
          <button
            onClick={handleRun}
            disabled={!validation.valid}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Play className="h-4 w-4" />
            Run
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Editor */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Validation Status Bar */}
          <div className={`flex items-center gap-2 px-4 py-2 text-sm ${
            validation.valid
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
          }`}>
            {validation.valid ? (
              <>
                <CheckCircle className="h-4 w-4" />
                <span>Valid JSON</span>
                <span className="text-gray-500 dark:text-gray-400">•</span>
                <span>{validation.stepCount} step{validation.stepCount !== 1 ? 's' : ''}</span>
              </>
            ) : (
              <>
                <AlertCircle className="h-4 w-4" />
                <span>{validation.errors[0]}</span>
                {validation.errors.length > 1 && (
                  <span className="text-gray-500 dark:text-gray-400">
                    (+{validation.errors.length - 1} more)
                  </span>
                )}
              </>
            )}
          </div>

          {/* Monaco Editor */}
          <div className="flex-1">
            <Editor
              height="100%"
              defaultLanguage="json"
              value={editorContent}
              theme={isDarkMode ? 'vs-dark' : 'light'}
              onChange={handleEditorChange}
              onMount={handleEditorMount}
              loading={
                <div className="flex items-center justify-center h-full text-gray-500">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  Loading editor...
                </div>
              }
              options={{
                minimap: { enabled: true },
                fontSize: 14,
                lineNumbers: 'on',
                folding: true,
                foldingHighlight: true,
                bracketPairColorization: { enabled: true },
                autoClosingBrackets: 'always',
                autoClosingQuotes: 'always',
                formatOnPaste: true,
                formatOnType: false,
                scrollBeyondLastLine: false,
                smoothScrolling: true,
                cursorBlinking: 'smooth',
                cursorSmoothCaretAnimation: 'on',
                renderWhitespace: 'none',
                quickSuggestions: true,
                suggestOnTriggerCharacters: true,
                tabSize: 2,
                wordWrap: 'on',
                automaticLayout: true,
                // Performance optimizations
                renderLineHighlight: 'line',
                occurrencesHighlight: 'off',
                selectionHighlight: false,
                // Scroll performance
                fastScrollSensitivity: 5,
                mouseWheelScrollSensitivity: 1,
              }}
            />
          </div>
        </div>

        {/* Sidebar - Preview & Metadata */}
        <div className={`w-80 border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex flex-col overflow-hidden transition-all ${
          showPreview ? 'translate-x-0' : 'translate-x-full w-0'
        }`}>
          {/* Toggle Preview */}
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="absolute right-80 top-1/2 -translate-y-1/2 p-1.5 bg-gray-200 dark:bg-gray-700 rounded-l-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors z-10"
            style={{ display: showPreview ? 'block' : 'none' }}
            title="Hide preview"
          >
            <EyeOff className="h-4 w-4 text-gray-600 dark:text-gray-300" />
          </button>

          {/* Metadata */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Project Details
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => {
                    setProjectName(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  className="w-full px-3 py-1.5 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Project name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Tags (comma separated)
                </label>
                <input
                  type="text"
                  value={projectTags}
                  onChange={(e) => {
                    setProjectTags(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  className="w-full px-3 py-1.5 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. finance, etl, quarterly"
                />
              </div>
            </div>
          </div>

          {/* Steps Preview */}
          <div className="flex-1 overflow-y-auto p-4">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Steps Preview
            </h3>
            {steps.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                {validation.valid ? 'No steps defined' : 'Fix JSON errors to see preview'}
              </p>
            ) : (
              <div className="space-y-2">
                {steps.map((step, index) => (
                  <div
                    key={step.id}
                    className="p-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="flex items-center justify-center w-5 h-5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded">
                        {step.index}
                      </span>
                      <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                        {step.name}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className={`px-1.5 py-0.5 rounded ${
                        step.reviewMode === 'user'
                          ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                          : step.reviewMode === 'auto'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}>
                        {step.reviewMode}
                      </span>
                      {step.hasDatasource && (
                        <span className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded">
                          DB
                        </span>
                      )}
                      {step.hasFiles && (
                        <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded">
                          files
                        </span>
                      )}
                      {step.hasOutput && (
                        <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                          output
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Quick Reference - Collapsible */}
            <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
              <button
                onClick={() => setShowQuickRef(!showQuickRef)}
                className="flex items-center gap-2 w-full text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                {showQuickRef ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                <BookOpen className="h-3 w-3" />
                Quick Reference
              </button>

              {showQuickRef && (
                <div className="mt-3 space-y-3 text-xs">
                  {/* Project-level options */}
                  <div>
                    <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-1.5">Project Options</h4>
                    <div className="space-y-1.5 pl-2 border-l-2 border-gray-200 dark:border-gray-600">
                      <RefItem name="project_name" type="string" desc="Display name for the project" required />
                      <RefItem name="description" type="string" desc="What this project does" />
                      <RefItem name="start_from" type="number" desc={<>Skip to step N (1-indexed). <b>start_from: 3</b> skips steps 1-2</>} />
                      <RefItem name="start_from_id" type="string" desc={<>Skip to step by ID. <b>start_from_id: "analysis"</b> skips earlier steps</>} />
                    </div>
                  </div>

                  {/* Defaults section */}
                  <div>
                    <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-1.5">defaults { }</h4>
                    <div className="space-y-1.5 pl-2 border-l-2 border-gray-200 dark:border-gray-600">
                      <RefItem name="context_mode" type="reset | continue | summary" desc={<><b>reset</b> = fresh REPL, <b>continue</b> = keep vars, <b>summary</b> = fresh + inject summary</>} />
                      <RefItem name="review_mode" type="auto | user | none" desc={<><b>auto</b> = AI self-reviews, <b>user</b> = pause for approval, <b>none</b> = continue immediately</>} />
                      <RefItem name="delay_ms" type="number" desc="Wait time (ms) before each step" />
                      <RefItem name="datasource" type="string" desc="Default database connection" />
                      <RefItem name="output_dir" type="string" desc="Default save location" />
                    </div>
                  </div>

                  {/* Step options */}
                  <div>
                    <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-1.5">steps [ { } ]</h4>
                    <div className="space-y-1.5 pl-2 border-l-2 border-gray-200 dark:border-gray-600">
                      <RefItem name="name" type="string" desc="Step display name" required />
                      <RefItem name="prompt" type="string" desc="Instructions for the AI" required />
                      <RefItem name="id" type="string" desc="Unique identifier (auto-generated)" />
                      <RefItem name="context_mode" type="enum" desc="Override default for this step" />
                      <RefItem name="review_mode" type="enum" desc="Override default for this step" />
                      <RefItem name="review_notes" type="string" desc={<>Guidance for <b>auto</b> review: what to verify/check</>} />
                      <RefItem name="success_criteria" type="string" desc="How to evaluate if step succeeded" />
                      <RefItem name="datasource" type="string" desc="Database for this step" />
                      <RefItem name="reference_files" type="string[]" desc="Files to read/analyze" />
                      <RefItem name="output_dir" type="string" desc="Save location for this step" />
                      <RefItem name="delay_ms" type="number" desc="Wait before this step" />
                      <RefItem name="skip" type="boolean" desc={<><b>true</b> = skip this step</>} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Delete Button */}
          {project && !project.isNew && !project.isSample && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => onDelete(project.id)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Delete Project
              </button>
            </div>
          )}
        </div>

        {/* Show Preview Button (when hidden) */}
        {!showPreview && (
          <button
            onClick={() => setShowPreview(true)}
            className="absolute right-0 top-1/2 -translate-y-1/2 p-1.5 bg-gray-200 dark:bg-gray-700 rounded-l-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            title="Show preview"
          >
            <Eye className="h-4 w-4 text-gray-600 dark:text-gray-300" />
          </button>
        )}
      </div>

      {/* Local Toast (bottom-right to avoid obscuring Save/Run buttons) */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-[60] animate-slide-in-right">
          <div className={`rounded-lg shadow-lg px-4 py-3 flex items-center gap-2 ${
            toast.type === 'success'
              ? 'bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 text-green-800 dark:text-green-200'
              : 'bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-200'
          }`}>
            {toast.type === 'success' ? (
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
            ) : (
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
            )}
            <span className="text-sm font-medium">{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
});

export default ProjectEditor;
